import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc, addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { resolveInviteToken } from '../lib/invites';
import { addGroupMember } from '../lib/firestore/groups';
import { createRSVP } from '../lib/firestore/events';
import { autoJoinDefaults } from '../lib/firestore/autoJoin';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../styles/Login.css';

const Signup = () => {
  const [phoneNumber, setPhoneNumber] = useState('+1');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for invite token
  const [inviteToken, setInviteToken] = useState(null);
  
  useEffect(() => {
    // Check URL params for invite token
    const tokenFromUrl = searchParams.get('invite');
    
    // Check location state for invite token
    const tokenFromState = location.state?.inviteToken;
    
    if (tokenFromUrl) {
      setInviteToken(tokenFromUrl);
    } else if (tokenFromState) {
      setInviteToken(tokenFromState);
    } else {
      // Check if we came from an invite link (stored in sessionStorage)
      const storedToken = sessionStorage.getItem('pendingInviteToken');
      if (storedToken) {
        setInviteToken(storedToken);
        sessionStorage.removeItem('pendingInviteToken');
      }
    }
  }, [searchParams, location]);

  // Convert phone number to email format for Firebase Auth
  const phoneToEmail = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return `${digits}@hotelia.local`;
  };

  // Process invite token - handles all invite types (group, event, contact)
  const processInviteToken = async (token, newUserId, newUserPhone, newUserEmail) => {
    try {
      const invite = await resolveInviteToken(token);
      
      if (!invite) {
        console.log('No valid invite token found');
        return; // Invalid or expired invite
      }

      // Handle different invite types
      if (invite.target.kind === 'group_join') {
        // Prepare member data
        const memberData = {
          userId: newUserId,
          role: invite.metadata?.role || 'member',
          joinedAt: new Date(),
        };
        
        // Only add sponsoredBy if it's a valid string
        const sponsorId = invite.metadata?.sponsorId || invite.issuedBy;
        if (sponsorId && typeof sponsorId === 'string') {
          memberData.sponsoredBy = sponsorId;
        }
        
        // Add user to group
        await addGroupMember(invite.target.id, memberData);
        
        // Store group ID to navigate after signup
        sessionStorage.setItem('pendingGroupJoin', invite.target.id);
        console.log('✅ User will be added to group after signup');
      } else if (invite.target.kind === 'event_join') {
        // Check if there's a stored RSVP choice from before signup
        const storedRSVP = sessionStorage.getItem('pendingRSVP');
        let rsvpState = 'going'; // default
        
        if (storedRSVP) {
          // Map stored choice to RSVP state
          if (storedRSVP === 'going') {
            rsvpState = 'going';
          } else if (storedRSVP === 'maybe') {
            rsvpState = 'interested';
          } else if (storedRSVP === 'no') {
            rsvpState = 'cancelled';
          }
          // Clear stored RSVP
          sessionStorage.removeItem('pendingRSVP');
        } else {
          // Use default based on invite metadata
          rsvpState = invite.metadata?.role === 'guest' ? 'interested' : 'going';
        }
        
        // RSVP to event with the selected choice
        await createRSVP({
          userId: newUserId,
          eventId: invite.target.id,
          state: rsvpState,
          plusOnes: 0,
        });
        
        // Store event ID to navigate after signup
        sessionStorage.setItem('pendingEventJoin', invite.target.id);
        console.log(`✅ User will be RSVPed to event with state: ${rsvpState}`);
      } else if (invite.target.kind === 'contact_invite') {
        // Add inviter to new user's contacts
        const inviterUserId = invite.target.id;
        
        // Get inviter's user data
        const inviterDoc = await getDoc(doc(db, 'users', inviterUserId));
        if (!inviterDoc.exists()) {
          return;
        }
        
        const inviterData = inviterDoc.data();
        
        // Add inviter to new user's contacts
        const contactsRef = collection(db, 'users', newUserId, 'contacts');
        
        // Check if already exists
        const inviterPhoneE164 = inviterData.phone || inviterData.phoneNumber;
        if (inviterPhoneE164) {
          const existingQuery = query(
            contactsRef,
            where('phoneE164', '==', inviterPhoneE164.startsWith('+') ? inviterPhoneE164 : `+${inviterPhoneE164}`)
          );
          const existing = await getDocs(existingQuery);
          
          if (existing.empty) {
            await addDoc(contactsRef, {
              name: `${inviterData.firstName || ''} ${inviterData.lastName || ''}`.trim() || 'Friend',
              phoneE164: inviterPhoneE164.startsWith('+') ? inviterPhoneE164 : `+${inviterPhoneE164}`,
              email: inviterData.email || null,
              note: `Invited you to Hotelia`,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
        
        // Also add new user to inviter's contacts
        if (newUserPhone) {
          const inviterContactsRef = collection(db, 'users', inviterUserId, 'contacts');
          const inviterExistingQuery = query(
            inviterContactsRef,
            where('phoneE164', '==', newUserPhone.startsWith('+') ? newUserPhone : `+${newUserPhone}`)
          );
          const inviterExisting = await getDocs(inviterExistingQuery);
          
          if (inviterExisting.empty) {
            await addDoc(inviterContactsRef, {
              name: 'Friend', // Will be updated when they set their name
              phoneE164: newUserPhone.startsWith('+') ? newUserPhone : `+${newUserPhone}`,
              email: newUserEmail || null,
              note: `Joined via your invite`,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
      }
    } catch (error) {
      console.error('Error processing invite token:', error);
      // Don't throw - this is a nice-to-have, but log for debugging
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate SMS consent
    if (!smsConsent) {
      setError('You must consent to receive SMS messages to create an account.');
      return;
    }
    
    setLoading(true);

    try {
      // Create account with phone number as email
      const authEmail = phoneToEmail(phoneNumber);
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, password);
      
      // Normalize phone to E.164 format
      const phoneE164 = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber.replace(/\D/g, '')}`;
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        phone: phoneE164,
        phoneNumber: phoneE164.replace(/\D/g, ''),
        email: email || null,
        createdAt: new Date(),
        firstName: '',
        lastName: '',
        profilePicture: '',
        smsConsent: true,
        smsConsentDate: new Date(),
        smsPreference: 'all', // Default to receiving all messages
      });

      // Update auth profile if email provided
      if (email) {
        await updateProfile(userCredential.user, {
          email: email,
        });
      }

      // Process invite token if present (handles group, event, or contact invites)
      if (inviteToken) {
        try {
          await processInviteToken(inviteToken, userCredential.user.uid, phoneE164, email);
        } catch (inviteError) {
          console.error('Error processing invite token:', inviteError);
          // Don't block signup if invite processing fails
        }
      }

      // Auto-join default groups and events
      try {
        await autoJoinDefaults(userCredential.user.uid);
      } catch (autoJoinError) {
        console.error('Error auto-joining defaults:', autoJoinError);
        // Don't block signup if auto-join fails
      }

      // Navigate based on pending invite
      const pendingGroup = sessionStorage.getItem('pendingGroupJoin');
      const pendingEvent = sessionStorage.getItem('pendingEventJoin');
      
      if (pendingGroup) {
        sessionStorage.removeItem('pendingGroupJoin');
        navigate(`/groups/${pendingGroup}?joined=true`);
      } else if (pendingEvent) {
        sessionStorage.removeItem('pendingEventJoin');
        navigate(`/events/${pendingEvent}?joined=true`);
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Hotelia</h1>
          <p>Create your account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <PhoneInput
              international
              defaultCountry="US"
              value={phoneNumber}
              onChange={setPhoneNumber}
              className="phone-input"
              id="phone"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              className="input"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email (Optional)</label>
            <input
              type="email"
              id="email"
              className="input"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                required
              />
              <span>
                I consent to receive SMS messages from Hotelia, including event invitations, 
                reminders, and account notifications. Message frequency varies. Message and data rates may apply. 
                Reply STOP to opt-out at any time.{' '}
                <a href="/sms-consent.html" target="_blank" rel="noopener noreferrer" className="link-inline">Learn more</a>.
                <br />
                <small style={{ marginTop: '0.5rem', display: 'block', color: '#666' }}>
                  <strong>We hate spam too!</strong> Hotelia will never spam you. Only users can send you messages.
                </small>
              </span>
            </label>
              <small className="form-hint" style={{ marginTop: '0.5rem', display: 'block' }}>
                By checking this box, you agree to our{' '}
                <a href="/terms.html" className="link-inline">Terms of Service</a> and{' '}
                <a href="/privacy.html" className="link-inline">Privacy Policy</a>.
              </small>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="link">
              Sign in
            </Link>
          </p>
          
          <div style={{ 
            marginTop: '2.5rem', 
            paddingTop: '1.5rem', 
            borderTop: '2px solid var(--border-color)', 
            textAlign: 'center',
            backgroundColor: 'var(--background-alt)',
            marginLeft: '-2.5rem',
            marginRight: '-2.5rem',
            marginBottom: '-2.5rem',
            paddingLeft: '2.5rem',
            paddingRight: '2.5rem',
            paddingBottom: '1.5rem',
            borderRadius: '0 0 var(--radius-xl) var(--radius-xl)'
          }}>
            <div style={{ 
              fontSize: '1rem', 
              marginBottom: '1rem',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <a href="/terms.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                Terms of Service
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <a href="/privacy.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                Privacy Policy
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <a href="/sms-consent.html" className="link" style={{ 
                color: 'var(--primary-color)',
                textDecoration: 'underline',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}>
                SMS Consent
              </a>
              <span style={{ color: 'var(--text-secondary)', fontSize: '1.2rem' }}>•</span>
              <Link 
                to="/support"
                className="link"
                style={{ 
                  color: 'var(--primary-color)',
                  textDecoration: 'underline',
                  fontWeight: '600',
                  fontSize: '0.95rem'
                }}
              >
                Support
              </Link>
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <a 
                href="https://apps.apple.com/us/app/hotelia-live/id6757988326" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-block',
                  textDecoration: 'none'
                }}
              >
                <img 
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1735689600" 
                  alt="Download on the App Store"
                  style={{
                    height: '60px',
                    width: 'auto',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;


